<template>
  <section class="section">
    <h2 class="title">
      {{ $t('route.users') }}
      <div class="level">
        <div class="level-left subtitle">
          {{ $t('subtitle.users') }}
        </div>
        <div class="level-right">
          <button
            class="button is-primary"
            @click.prevent="handleShowFilter"
            :class="{ 'is-info': hasActiveFilters }"
          >
            <octicon :icon="search" />
            <span>{{ $t('users.filter.title') }}</span>
            <span v-if="hasActiveFilters" class="tag is-light ml-1">{{ activeFilterCount }}</span>
          </button>
          <button class="button is-primary ml-2" @click.prevent="handleCreateUser">
            <octicon :icon="plus" /><span>{{ $t('users.addUser') }}</span>
          </button>
        </div>
      </div>
    </h2>

    <!-- Users Table -->
    <user-list
      ref="userList"
      :search-filter="searchFilter"
      :filters="userFilters"
      :theme="$_ui_theme_tables"
      :per-page="10"
      @user-edit="handleEditUser"
      @user-reset-password="handleResetPassword"
      @user-toggle-status="handleToggleUserStatus"
      @user-force-logout="handleForceLogout"
    />
    <!-- User Form Modal -->
    <user-form
      v-if="showUserForm"
      :show.sync="showUserForm"
      :user="selectedUser"
      :current-user="user"
      @user-saved="handleUserSaved"
    />

    <!-- Password Reset Modal -->
    <password-reset-modal
      :show.sync="showPasswordResetModal"
      :user="passwordResetUser"
      @password-reset="handlePasswordReset"
    />

    <!-- Force Logout Confirmation Modal -->
    <force-logout-confirmation
      :show.sync="showForceLogoutModal"
      :user="forceLogoutUser"
      @confirm="handleForceLogoutConfirm"
      @cancel="handleForceLogoutCancel"
    />

    <!-- User Activities Table -->
    <user-activities-table :theme="$_ui_theme_tables" />

    <!-- User Filter Modal -->
    <user-filter
      :show.sync="showUserFilter"
      :filters="userFilters"
      @apply-filters="handleApplyFilters"
    />
  </section>
</template>
<script>
import { plus, search } from 'octicons-vue'
import { debounce } from '@/utils'
import { UserList, UserForm, PasswordResetModal, UserActivitiesTable, UserFilter } from '@/components/users'
import ForceLogoutConfirmation from '@/components/users/ForceLogoutConfirmation.vue'

export default {
  components: {
    UserList,
    UserForm,
    PasswordResetModal,
    UserActivitiesTable,
    UserFilter,
    ForceLogoutConfirmation
  },
  data () {
    return {
      searchFilter: '',
      showUserForm: false,
      showPasswordResetModal: false,
      showForceLogoutModal: false,
      showUserFilter: false,
      selectedUser: null,
      passwordResetUser: null,
      forceLogoutUser: null,
      userFilters: {
        scopes: [],
        statuses: []
      },
      actualFilteredCount: 0 // Track actual number of filtered results
    }
  },

  computed: {
    plus () {
      return plus
    },
    search () {
      return search
    },
    user () {
      return this.$store.getters['authentication/user']
    },
    /**
     * Check if any filters are currently active
     */
    hasActiveFilters() {
      return this.actualFilteredCount > 0
    },
    /**
     * Count of active filters for display - show the actual result count from filter modal
     */
    activeFilterCount() {
      return this.actualFilteredCount
    }
  },
  watch: {
    /**
     * Watch for changes in search filter and trigger debounced search
     * This ensures API calls are made when user types in the search field
     * with proper debouncing to prevent excessive requests
     */
    searchFilter: {
      handler (newValue, oldValue) {
        // Only trigger search if the value actually changed
        if (newValue !== oldValue) {
          this.debouncedSearch()
        }
      },
      immediate: false
    },
    /**
     * Watch for changes in user filters and trigger table reload
     * This ensures the table updates when filters are applied
     */
    userFilters: {
      handler (newValue, oldValue) {
        // Only trigger reload if filters actually changed
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          // Use $nextTick to ensure props have been updated before reloading
          this.$nextTick(() => {
            this.reloadUserList()
          })
        }
      },
      deep: true,
      immediate: false
    }
  },
  created () {
    // Create debounced search function to prevent excessive API calls
    this.debouncedSearch = debounce(() => {
      // Trigger table reload when search filter changes
      if (this.$refs.userList) {
        this.$refs.userList.reloadTable()
      }
    }, 300) // 300ms debounce delay
  },
  methods: {
    /**
     * Handles creating a new user
     */
    handleCreateUser () {
      this.selectedUser = null
      this.showUserForm = true
    },
    /**
     * Handles showing the filter modal
     */
    handleShowFilter () {
      this.showUserFilter = true
    },
    /**
     * Handles applying filters from the filter modal
     */
    handleApplyFilters (filters) {
      this.userFilters = {
        scopes: [...filters.scopes],
        statuses: [...filters.statuses]
      }

      // Use the result count from the filter modal
      this.actualFilteredCount = filters.resultCount || 0

      // Note: reloadUserList() is called by the userFilters watcher
      // to ensure proper timing and avoid race conditions
    },
    /**
     * Handles editing an existing user
     */
    handleEditUser (user) {
      this.selectedUser = user
      this.showUserForm = true
    },
    /**
     * Handles password reset request
     */
    handleResetPassword (user) {
      this.passwordResetUser = user
      this.showPasswordResetModal = true
    },
    /**
     * Handles user status toggle
     */
    handleToggleUserStatus (user) {
      const isActive = user.status === 'active'
      const action = isActive ? this.$t('users.deactivate') : this.$t('users.activate')
      const confirmMessage = this.$t('users.confirmToggleStatus', { action: action, name: user.name })

      if (!window.confirm(confirmMessage)) {
        return
      }

      const endpoint = isActive ? 'deactivate' : 'activate'

      return this.$http.patch(`/api/v1/users/${user.id}/${endpoint}`).then(body => {
        if (!body || !body.data) {
          return
        }

        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: body.data.message || this.$t('users.userStatusUpdateSuccess', { action: action })
        })

        // Reload the table to reflect changes
        this.reloadUserList()
      }).catch(error => {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('users.userStatusUpdateError', { action: action })
        })
      })
    },
    /**
     * Handles force logout request
     */
    handleForceLogout (user) {
      this.forceLogoutUser = user
      this.showForceLogoutModal = true
    },
    /**
     * Handles force logout confirmation
     */
    handleForceLogoutConfirm () {
      if (!this.forceLogoutUser) {
        return
      }

      return this.$http.post(`/api/v1/users/${this.forceLogoutUser.id}/force-logout`).then(body => {
        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: body.data?.message || this.$t('users.forceLogoutSuccess', { name: this.forceLogoutUser.name })
        })

        // Reload the table to reflect changes
        this.reloadUserList()
      }).catch(error => {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('users.forceLogoutError', { name: this.forceLogoutUser.name })
        })
      }).finally(() => {
        this.forceLogoutUser = null
      })
    },
    /**
     * Handles force logout cancellation
     */
    handleForceLogoutCancel () {
      this.forceLogoutUser = null
    },
    /**
     * Handles user saved event from UserForm
     */
    handleUserSaved () {
      this.reloadUserList()
    },
    /**
     * Handles password reset event from PasswordResetModal
     */
    handlePasswordReset () {
      this.reloadUserList()
    },
    /**
     * Reloads the user list table
     */
    reloadUserList () {
      if (this.$refs.userList) {
        this.$refs.userList.reloadTable()
      }
    }
  }
}
</script>

<style scoped>
.button.is-info {
  border-color: var(--primary-color, #1890ff) !important;
  border-width: 2px !important;
  background: rgba(24, 144, 255, 0.1) !important;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1) !important;
  color: var(--primary-color, #1890ff) !important;
}

.button.is-info:hover {
  background: rgba(24, 144, 255, 0.15) !important;
}
</style>
